import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icon, Slider, Tooltip } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { EMAIL_DESIGN_ELEMENTS_SIDEBAR } from '../../EmailDesignPage/EmailBuilder/translationValues';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { deepCopy } from '../../../utils/javascript';
import TextBlockWithQuillRenderer from '../../ComplexDocumentDesign/TextBlockWithQuillRenderer';
import { constructAutoSaveJSON, constructQuillJSON, convertContractToReact } from '../../../utils/TextBlockHelper';
import { useEffect } from 'react';
import { COMPLEX_EMAIL_SETTINGS } from '../translationValues';

const dummyCallback = () => {};

export const SideBarSettings = styled.div`
  padding: 1.5em 10px;
  width: 100%;
  background-color: #fff;
  overflow: auto;
  height: calc(100dvh - 50px);
  ::-webkit-scrollbar {
    display: none;
  }

  .ant-slider {
    min-width: 250px;
    display: inline-block;
    margin-right: 15px;
    margin-left: 15px;

    .ant-slider-track {
      background-color: rgb(219 57 31 / 90%);
    }

    .ant-slider-handle {
      border-color: rgb(219 57 31 / 90%);

      &:focus {
        box-shadow: 0 0 0 5px rgb(219 57 31 / 20%);
      }
    }

    &:hover {
      .ant-slider-track {
        background-color: rgb(219 57 31 / 90%);
      }

      .ant-slider-handle,
      .ant-slider-handle.ant-slider-handle,
      .ant-slider-handle:not(.ant-tooltip-open) {
        border-color: rgb(219 57 31 / 90%);
      }
    }
  }

  .ql-container.ql-snow {
    border: 1px solid #ccc;
    > .ql-editor {
      padding: 12px 15px;
      min-height: 48px;
    }
  }
`;

export const TabMenuHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => (props.inverted ? '#333' : '#fff')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: ${props =>
    props.subject ? '5px 0 0 0' : props.isImageCount ? '60px 10px 10px 10px' : '10px 10px 10px'};
  text-transform: uppercase;
  text-align: center;
  display: flex;
  justify-content: center;
  position: relative;
  &.categoryName {
    margin: 20px auto;
    &:before {
      content: '';
      display: block;
      width: 65px;
      height: 2px;
      background: #fff;
      left: 25px;
      top: 50%;
      position: absolute;
    }
    &:after {
      content: '';
      display: block;
      width: 65px;
      height: 2px;
      background: #fff;
      right: 25px;
      top: 50%;
      position: absolute;
    }
    h2 {
      font-size: 14px;
      font-weight: 700;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 10px 10px 10px;
      text-transform: capitalize;
      text-align: center;
      max-width: 115px;
      color: ${props => (props.inverted ? '#333' : '#ffffff')};
    }
  }
  .see-all {
    cursor: pointer;
    font-weight: 400;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const defaultElContents = [{
  content: '',
  contentType: "PARAGRAPH",
  style: {
    color: "#000000",
  },
},
{
  contentType: "LINE_BREAK",
  style: {
    lineSpacing: 1.5
  }
}
];

const QuillAddressRender = ({
  randomKey,
  globalVariables,
  elContents,
  updateContent,
  elIndex,
  contentType,
  uuid
}) => {
  const [textEditable, setTextEditable] = useState(false);
  const [selected, setSelected] = useState(false);

  return (
    <div onClick={() => setSelected(true)}>
      <TextBlockWithQuillRenderer
        key={randomKey}
        defaultContent={constructQuillJSON(elContents, null, null, globalVariables)}
        saveBlock={(...rest) =>
          updateContent(...rest, elIndex, { itemUUIDs: contentType + '-' + uuid, isSummary: false })
        }
        itemUUID={contentType + '-' + uuid}
        selected={selected}
        enableSideBar={dummyCallback}
        enableAI={false}
        toolbarInvisible={true}
        adjustScroll={dummyCallback}
        textEditable={textEditable}
        setTextEditable={setTextEditable}
        toolbarDOM={'link-block-toolbar'}
        variableDOM={`link-block-variable${elIndex}`}
      >
        {!textEditable ? convertContractToReact(elContents, 'Arial', 14, globalVariables) : null}
      </TextBlockWithQuillRenderer>
    </div>
  );
};

const QuillReadAsRender = ({
  randomKey,
  globalVariables,
  elContents,
  updateContent,
  elIndex,
  contentType,
  uuid,
}) => {
  const [textEditable, setTextEditable] = useState(false);
  const [selected, setSelected] = useState(false);

  return (
    <div onClick={() => setSelected(true)}>
      <TextBlockWithQuillRenderer
        style={{ marginTop: '4px' }}
        key={randomKey + '-summary'}
        defaultContent={constructQuillJSON(
          elContents || defaultElContents,
          null,
          null,
          globalVariables,
        )}
        saveBlock={(...rest) =>
          updateContent(...rest, elIndex, {
            itemUUIDs: contentType + '-' + uuid + '-summary',
            isSummary: true,
          })
        }
        itemUUID={contentType + '-' + uuid + '-summary'}
        selected={selected}
        enableSideBar={dummyCallback}
        enableAI={false}
        toolbarInvisible={true}
        adjustScroll={dummyCallback}
        textEditable={textEditable}
        setTextEditable={setTextEditable}
        toolbarDOM={'link-block-toolbar'}
        variableDOM={`link-block-variable${elIndex}`}
      >
      {!textEditable
        ? convertContractToReact(
            elContents || defaultElContents,
            'Arial',
            14,
            globalVariables,
          )
        : null}
        </TextBlockWithQuillRenderer>
    </div>
  );
};

export const NAMESPACES = ['EMAIL_DESIGN_ELEMENTS_SIDEBAR'];

const LinkBlockSidebarSettings = ({ linkBlock, parentUUID }) => {
  const { contents: blockContents, style, uuid, type } = linkBlock;
  const { spacing } = style;

  const [canUpdateRandomKey, setCanUpdateRandomKey] = useState(true);
  const { t } = useTranslation(NAMESPACES);

  const variablesSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables, store] = useDocumentContext(
    variablesSelector,
    `LinkBlockSidebar-${uuid}`,
  );

  const [randomKey, setRandomKey] = useState(1);
  useEffect(() => {
    if (canUpdateRandomKey)
      setRandomKey(value => value + 1);
  }, [blockContents])

  const updateSpacing = newValue => {
    store.updateBlock('spacing', uuid, {
      spacing: newValue,
      flowUUID: parentUUID,
      type: type,
    });
  };

  const updateContent = useCallback((
    elContents,
    undoDiff,
    redoDiff,
    cb,
    ref,
    op = undefined,
    newGlobalVariables = [],
    index,
    args
  ) => {
    const existingContent = deepCopy(store.get()[uuid].contents);
    let newContents = constructAutoSaveJSON(elContents, undefined, undefined, newGlobalVariables);
    deepCopy(blockContents).map((el, elIndex) => {
      if (elIndex === index) {
        if (args.isSummary) {
          existingContent[index].summary = deepCopy(newContents);
        } else { 
          if(newContents[0].content === '\n') {
            if(existingContent[index].summary && existingContent[index].summary[0]) {
              existingContent[index].summary[0].content = '';
            }
          }
          existingContent[index].contents = newContents;
        }
      }
      return el;
    });
    store.updateBlock('link-block', uuid, {
      contents: existingContent,
      flowUUID: parentUUID,
      type: 'block',
      historyChange: {
        undo: undoDiff,
        redo: redoDiff,
        op: op,
      },
    });
  }, []);

  return (
    <SideBarSettings onMouseEnter={() => setCanUpdateRandomKey(false)} onMouseLeave={() => setCanUpdateRandomKey(true)}>
      <TabMenuHeader inverted={1}>
        {t(
          EMAIL_DESIGN_ELEMENTS_SIDEBAR.SPACING_BETWEEN_SOCIAL_LINKS_LABEL.key,
          EMAIL_DESIGN_ELEMENTS_SIDEBAR.SPACING_BETWEEN_SOCIAL_LINKS_LABEL.value,
        )}
      </TabMenuHeader>
      <div>
        <Slider value={spacing} disabled={false} min={10} max={100} onChange={updateSpacing} />
        <span> {spacing} </span>
      </div>
      <div id="link-block-toolbar" style={{ visibility: 'hidden', height: 0 }} />
      {blockContents.map((blockEl, elIndex) => {
        const { contentType, contents: elContents, summary } = blockEl;
        return (
          <>
            <TabMenuHeader inverted={1}>{contentType!=='TWITTER' ? contentType : 'X (Formerly Twitter)' }</TabMenuHeader>
            <div id={`link-block-variable${elIndex}`} style={{ position: 'relative', top: 10 }} />
            <div> {t(
              EMAIL_DESIGN_ELEMENTS_SIDEBAR.WEB_ADDRESS_LABEL.key,
              EMAIL_DESIGN_ELEMENTS_SIDEBAR.WEB_ADDRESS_LABEL.value,
            )}:
            </div>
            <QuillAddressRender
              randomKey={randomKey}
              globalVariables={globalVariables}
              updateContent={updateContent}
              contentType={contentType}
              uuid={uuid}
              elIndex={elIndex}
              elContents={elContents}
            />
            <div style={{ marginTop: '4px', display: elContents[0]?.content && elContents[0]?.content !=='\n'? 'block' : 'none' }}>
            <div style={{ marginTop: '4px'}} > {t(
              EMAIL_DESIGN_ELEMENTS_SIDEBAR.READ_AS_LABEL.key,
              EMAIL_DESIGN_ELEMENTS_SIDEBAR.READ_AS_LABEL.value,
            )} <Tooltip
                placement="top"
                title={t(COMPLEX_EMAIL_SETTINGS.READ_AS_SUMMARY_TOOLTIP.key, COMPLEX_EMAIL_SETTINGS.READ_AS_SUMMARY_TOOLTIP.value)}>
                <Icon type="question-circle" />
              </Tooltip> <span style={{display: 'inline-block', marginTop: '-4px'}}>:</span>
            </div>
            <QuillReadAsRender
              randomKey={randomKey}
              globalVariables={globalVariables}
              updateContent={updateContent}
              contentType={contentType}
              uuid={uuid}
              elIndex={elIndex}
              elContents={summary}
            />
            </div>
          </>
        );
      })}
    </SideBarSettings>
  );
};

export const LinkBlockSidebar = ({ closeSidebar, ...props }) => {
  return (
    <>
      {ReactDOM.createPortal(
        <LinkBlockSidebarSettings {...props} />,
        document.getElementById('sidebar-absolute-container'),
      )}
      {ReactDOM.createPortal(
        <div
          className="layoutbtn-container"
          onClick={closeSidebar}
          style={{ position: 'absolute', top: 0, left: -2, zIndex: 1 }}
        >
          <Icon type={'close'} />
        </div>,
        document.getElementById('sidebar-absolute-close'),
      )}
    </>
  );
};
